/**
 * Het 20%-kortingsaanbod van boenkop.be (popup op /studiocamera en /twee-uur)
 * en de toon van de portaalhero, afgeleid van waar de bezoeker vandaan komt.
 *
 * - De kortingsmail linkt naar het portaal met `?code=<code>` (lead-handler.php).
 *   Die code bewaren we voor deze browsersessie: de home toont dan dat de korting
 *   klaarstaat en de betaalpagina vult het codeveld alvast in. Toepassen blijft
 *   een klik van de klant; de backend valideert de code zoals altijd.
 *   De code is enkel voor nieuwe klanten: wie al een bevestigde boeking heeft
 *   (has_confirmed_booking), krijgt de notitie niet en het veld blijft leeg.
 * - Wie via /studiocamera (src=) of via de kortingsmail van /studiocamera komt,
 *   krijgt de gezinstoon van die pagina in plaats van de algemene studiotekst.
 *
 * sessionStorage, geen cookie: zelfde keuze als utils/attribution.ts.
 */
import { getAttribution, sourcePagePath } from './attribution'

const STORAGE_KEY = 'boenkop_lead_code'

// Kortingscodes zijn korte tokens (admin > kortingscodes); al de rest negeren we.
const CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/

export type HeroVariant = 'studiocamera' | 'default'

function sanitizeCode(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const code = value.trim().toUpperCase()
  return CODE_PATTERN.test(code) ? code : null
}

/** Lees `code` uit de URL en bewaar hem voor deze sessie. Geeft de bekende code terug. */
export function captureLeadCode(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): string | null {
  const code = sanitizeCode(new URLSearchParams(search).get('code'))
  if (code) {
    try {
      sessionStorage.setItem(STORAGE_KEY, code)
    } catch {
      // Storage kan uitstaan (privémodus): dan tonen we gewoon geen notitie.
    }
    return code
  }
  return getLeadCode()
}

/** De kortingscode uit de kortingsmail, als die in deze sessie binnenkwam. */
export function getLeadCode(): string | null {
  try {
    return sanitizeCode(sessionStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

/** Vergeet de code, bv. nadat hij op een boeking is toegepast (hij geldt maar één keer). */
export function clearLeadCode(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage uit: niets te wissen.
  }
}

/** Welke herotekst de portaalhome toont. */
export function heroVariant(): HeroVariant {
  if (sourcePagePath() === '/studiocamera') return 'studiocamera'
  const { utm_source, utm_campaign } = getAttribution()
  if (utm_source === 'lead-mail' && utm_campaign === 'studiocamera') return 'studiocamera'
  return 'default'
}
